export interface Web3FormsResponse {
  success: boolean
  message: string
  data?: any
}

// Web3Forms Access Key for info@sponsrai.se
// All form submissions will be sent to this email
const WEB3FORMS_ACCESS_KEY = '9d55d263-83bb-4e6f-b05a-3b8d38836fdc'

/**
 * Submits form data to Web3Forms
 * @param data The form data object
 * @param subject Optional subject line for the email
 * @returns Promise resolving to the response
 */
export const submitToWeb3Forms = async (
  data: Record<string, any>,
  subject?: string,
): Promise<Web3FormsResponse> => {
  try {
    const formData = new FormData()

    // Add access key
    formData.append('access_key', WEB3FORMS_ACCESS_KEY)

    // Add subject if provided
    if (subject) {
      formData.append('subject', subject)
    }

    // Add from_name to make emails look professional
    formData.append('from_name', 'SponsrAI Platform')

    // Add all data fields
    Object.keys(data).forEach((key) => {
      const value = data[key]
      if (Array.isArray(value)) {
        formData.append(key, value.join(', '))
      } else if (value instanceof File) {
        formData.append(key, value)
      } else if (value !== null && value !== undefined) {
        formData.append(key, String(value))
      }
    })

    const response = await fetch('https://api.web3forms.com/submit', {
      method: 'POST',
      body: formData,
    })

    const result = await response.json()

    return {
      success: result.success,
      message:
        result.message ||
        (result.success
          ? 'Form submitted successfully'
          : 'Failed to submit form'),
      data: result,
    }
  } catch (error) {
    console.error('Web3Forms submission error:', error)
    return {
      success: false,
      message:
        'An error occurred while submitting the form. Please try again later.',
    }
  }
}

/**
 * Submit a brand registration notification
 */
export const submitBrandRegistration = async (
  brandData: Record<string, any>,
): Promise<Web3FormsResponse> => {
  return submitToWeb3Forms(brandData, 'New Brand Registration - SponsrAI')
}

/**
 * Submit an organizer registration notification
 */
export const submitOrganizerRegistration = async (
  organizerData: Record<string, any>,
): Promise<Web3FormsResponse> => {
  return submitToWeb3Forms(
    organizerData,
    'New Organizer Registration - SponsrAI',
  )
}

/**
 * Submit a help/support request
 */
export const submitHelpRequest = async (
  helpData: Record<string, any>,
): Promise<Web3FormsResponse> => {
  return submitToWeb3Forms(helpData, 'Help Request - SponsrAI')
}

/**
 * Submit a contact form
 */
export const submitContactForm = async (
  contactData: Record<string, any>,
): Promise<Web3FormsResponse> => {
  return submitToWeb3Forms(contactData, 'Contact Form - SponsrAI')
}
